import React, { useEffect, useRef } from "react";

import { videoDataListsType } from "../shared/type";

// Video card component with hover/mousedown preview
const VideoCard: React.FC<{ item: videoDataListsType }> = ({ item }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPreviewingRef = useRef(false);
  const wasPlayingRef = useRef(false);

  const startPreview = () => {
    const video = videoRef.current;
    if (!video || isPreviewingRef.current) return;

    // Don't start preview if video is already playing (user interaction)
    if (!video.paused) {
      wasPlayingRef.current = true;
      return;
    }

    isPreviewingRef.current = true;
    wasPlayingRef.current = false;

    // Reset video to start and play
    video.currentTime = 0;
    video.play().catch(() => {
      // Handle autoplay restrictions
      isPreviewingRef.current = false;
    });

    // Pause after 1.5 seconds (between 1-2s)
    previewTimeoutRef.current = setTimeout(() => {
      if (video && isPreviewingRef.current) {
        video.pause();
        video.currentTime = 0; // Reset to beginning
        isPreviewingRef.current = false;
      }
    }, 1500);
  };

  const stopPreview = () => {
    const video = videoRef.current;
    if (!video) return;

    // Clear timeout if preview is still running
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }

    // Reset video if previewing (not if user was playing)
    if (isPreviewingRef.current && !wasPlayingRef.current) {
      video.pause();
      video.currentTime = 0;
      isPreviewingRef.current = false;
    }

    wasPlayingRef.current = false;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full h-full flex flex-col bg-black rounded-xl">
      <div
        className="w-full h-full rounded-t-xl overflow-hidden"
        onMouseEnter={startPreview}
        onMouseLeave={stopPreview}
        onMouseDown={startPreview}
      >
        <video
          ref={videoRef}
          src={item.videoUrl}
          controls
          controlsList="nodownload noremoteplayback" // nofullscreen
          disableRemotePlayback={true}
          disablePictureInPicture
          className="w-full h-full object-cover rounded-t-xl"
          preload="metadata"
        />
      </div>
      <div className="w-full xl:py-4 py-3 flex justify-center items-center bg-black/100 backdrop-blur-md rounded-b-xl cursor-pointer">
        <h6 className="xl:text-base lg:text-sm md:text-sm sm:text-sm text-base md:font-semibold font-bold text-white text-center line-clamp-1 pr-2 pl-[2px]">
          {item.title}
        </h6>
      </div>
    </div>
  );
};

export default VideoCard;
